import { getRepositoryUploadStates } from '#pplx/frontend/apps/ask/queries/file-queries/getRepositoryUploadStates.js';
import { getRepositoryUploadStatesQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/fileKeys.js';
import { secondsToMilliseconds } from '#pplx/frontend/i18n/datetime.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useRef } from 'react';
const PROJECT_SIZE_LIMIT_EXCEEDED = 'project_size_limit_exceeded';
const IMPORT_WORK_LIMIT_EXCEEDED = 'import_work_limit_exceeded';
const useExponentialPolling = ({ initialIntervalMs, maxIntervalMs = 60_000, timeoutMs = 900_000, // 15 minutes
 }) => {
    const pollingStartTimeRef = useRef(null);
    const getInterval = () => {
        // Initialize polling start time
        if (pollingStartTimeRef.current === null) {
            pollingStartTimeRef.current = Date.now();
            return initialIntervalMs;
        }
        const elapsedMs = Date.now() - pollingStartTimeRef.current;
        const elapsedMinutes = Math.floor(elapsedMs / 60_000);
        // Timeout after specified duration
        if (elapsedMs >= timeoutMs) {
            return false;
        }
        // Calculate exponential backoff: double every minute until reaching max
        let currentInterval = initialIntervalMs;
        for (let i = 0; i < elapsedMinutes; i++) {
            if (currentInterval < maxIntervalMs) {
                currentInterval = Math.min(currentInterval * 2, maxIntervalMs);
            }
        }
        return currentInterval;
    };
    const reset = () => {
        pollingStartTimeRef.current = null;
    };
    return { getInterval, reset };
};
const UPLOAD_STATUS_ACTIVE_POLLING_INTERVAL_MS = 5_000;
const UPLOAD_STATUS_PASSIVE_POLLING_INTERVAL_MS = 60_000;
export const useRepositoryUploadStates = ({ reason, fileRepoInfo, enablePolling = false, forceActivePolling = false, }) => {
    const queryKey = getRepositoryUploadStatesQueryKey(fileRepoInfo);
    const { getInterval, reset } = useExponentialPolling({
        initialIntervalMs: UPLOAD_STATUS_ACTIVE_POLLING_INTERVAL_MS,
    });
    const { data } = useQuery({
        queryKey,
        queryFn: () => {
            return getRepositoryUploadStates({
                request: {
                    file_repository_info: fileRepoInfo,
                },
                reason,
            });
        },
        refetchInterval: (query) => {
            if (!enablePolling) {
                reset();
                return false;
            }
            const { pending_files = 0 } = query.state.data?.summary ?? {};
            const passivePolling = pending_files === 0 && !forceActivePolling;
            if (passivePolling) {
                reset();
                return UPLOAD_STATUS_PASSIVE_POLLING_INTERVAL_MS;
            }
            return getInterval();
        },
        refetchIntervalInBackground: false,
        staleTime: secondsToMilliseconds(30),
    });
    return {
        total: data?.summary?.total_files ?? 0,
        pending: data?.summary?.pending_files ?? 0,
        errors: data?.summary?.error_files ?? 0,
        uploads: data?.summary?.total_uploads ?? 0,
        projectOverSizeLimit: data?.uploads.some((upload) => upload.terminal_reason === PROJECT_SIZE_LIMIT_EXCEEDED) ??
            false,
        projectImportWorkLimitExceeded: data?.uploads.some((upload) => upload.terminal_reason === IMPORT_WORK_LIMIT_EXCEEDED) ??
            false,
    };
};
//# sourceMappingURL=useRepositoryUploadStates.js.map